import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import { useMsal } from '@azure/msal-react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useIdleTimer } from 'react-idle-timer'
import { InteractionStatus } from '@azure/msal-browser'

import { config } from '../config'
import { useAuth } from '../providers/AuthProvider'
import { FormTitle } from '../components/styles/Text'
import { FlexColumn } from '../components/styles/Containers'
import SupportPopper from '../components/common/SupportPopper'
import Sidebar from '../components/common/Sidebar'
import Loading from '../components/common/Loading'

const Page = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
`

const Body = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  min-height: calc(100vh - 56px);
  height: 100%;
`

const SidebarContainer = styled.div`
  width: max-content;
`

const HelpContainer = styled.div`
  position: fixed;
  right: 28px;
  bottom: 28px;
`

const supportProps = {
  text: 'Reach out to us with any technical questions or issues:',
  email: 'support@vvctec.com',
  phone: '(250) 544-9578',
  supportPortal: config.supportPortal ?? false
}

const TIMEOUT_INTERVAL = 1000 * 60 * 20

export const ProtectedRoute = () => {
  const { authState } = useAuth()
  const { inProgress } = useMsal()
  const navigate = useNavigate()

  const loc = useLocation()

  const onIdle = () => navigate('/logout', { replace: true })

  useIdleTimer({
    onIdle,
    timeout: TIMEOUT_INTERVAL,
    crossTab: true,
    leaderElection: true,
    syncTimers: 200,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
      'visibilitychange',
      'focus'
    ]
  })

  useEffect(() => {
    if (!authState.isLoggedIn && !authState.loggingOut) {
      navigate('/login?dest=' + loc.pathname + loc.search, { replace: true })
    }
  }, [authState, loc])

  if (authState.loggingOut) {
    return (
      <Page>
        <Body>
          <FlexColumn>
            <Loading>
              <FormTitle>Logging out...</FormTitle>
            </Loading>
          </FlexColumn>
        </Body>
      </Page>
    )
  }

  if (inProgress === InteractionStatus.Startup) {
    return (
      <Page>
        <Body>
          <Loading />
        </Body>
      </Page>
    )
  }

  if (!authState.isLoggedIn) {
    return null
  }

  return (
    <Page>
      <SidebarContainer>
        <Sidebar />
      </SidebarContainer>
      <Body>
        <Outlet />
        <HelpContainer>
          <SupportPopper {...supportProps} />
        </HelpContainer>
      </Body>
    </Page>
  )
}
